import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatusChip, LoadingBlock } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtCurrency, fmtDate } from "@/lib/utils-app";
import { LogIn, LogOut } from "lucide-react";

const FILTERS = ["all", "reserved", "checked_in", "checked_out", "cancelled", "no_show"];

export default function Reservations() {
    const [items, setItems] = useState(null);
    const [status, setStatus] = useState("all");
    const [q, setQ] = useState("");

    const load = async () => {
        const params = {};
        if (status !== "all") params.status = status;
        if (q) params.q = q;
        const { data } = await api.get("/reservations", { params });
        setItems(data);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [status]);

    const checkIn = async (id) => {
        await api.post(`/reservations/${id}/check-in`);
        toast.success("Checked in");
        load();
    };
    const checkOut = async (id) => {
        await api.post(`/reservations/${id}/check-out`);
        toast.success("Checked out · housekeeping task created");
        load();
    };

    return (
        <>
            <PageHeader
                title="Reservations"
                subtitle="All stays, in-house and upcoming."
                actions={
                    <div className="flex gap-2">
                        <Input
                            placeholder="Search name or room"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && load()}
                            className="w-56"
                            data-testid="reservations-search"
                        />
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger className="w-44" data-testid="reservations-status">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {FILTERS.map((s) => (
                                    <SelectItem key={s} value={s} className="capitalize">
                                        {s.replace("_", " ")}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                }
            />
            {!items ? (
                <LoadingBlock rows={6} />
            ) : (
                <Card>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="reservations-table">
                                <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
                                    <tr>
                                        <th className="text-left py-2.5 px-4">Guest</th>
                                        <th className="text-left py-2.5 px-4">Room</th>
                                        <th className="text-left py-2.5 px-4">Check-in</th>
                                        <th className="text-left py-2.5 px-4">Check-out</th>
                                        <th className="text-left py-2.5 px-4">Source</th>
                                        <th className="text-left py-2.5 px-4">Total</th>
                                        <th className="text-left py-2.5 px-4">Status</th>
                                        <th className="text-right py-2.5 px-4">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((r) => (
                                        <tr key={r.id} className="border-t border-border hover:bg-secondary/50" data-testid="reservations-row">
                                            <td className="py-2.5 px-4 font-medium">{r.guest_name}</td>
                                            <td className="py-2.5 px-4">{r.room_number}</td>
                                            <td className="py-2.5 px-4">{fmtDate(r.check_in)}</td>
                                            <td className="py-2.5 px-4">{fmtDate(r.check_out)}</td>
                                            <td className="py-2.5 px-4">{r.source}</td>
                                            <td className="py-2.5 px-4 tabular">{fmtCurrency(r.total)}</td>
                                            <td className="py-2.5 px-4">
                                                <StatusChip status={r.status} />
                                            </td>
                                            <td className="py-2.5 px-4 text-right">
                                                {r.status === "reserved" && (
                                                    <Button size="sm" variant="outline" onClick={() => checkIn(r.id)} data-testid="reservations-checkin">
                                                        <LogIn className="w-3.5 h-3.5 mr-1" /> Check in
                                                    </Button>
                                                )}
                                                {r.status === "checked_in" && (
                                                    <Button size="sm" variant="outline" onClick={() => checkOut(r.id)} data-testid="reservations-checkout">
                                                        <LogOut className="w-3.5 h-3.5 mr-1" /> Check out
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}
        </>
    );
}
